import { ActivityIndicator, Image, Pressable, Text, View } from "react-native";
import useFileSelector from "@/models/useFileSelector";
import PageContainer from "@/ui/PageContainer";
import { useTranslation } from "react-i18next";
import { useRouter } from "@/_router";
import { ms } from "@/ui/moderateScale";
import TextareaAutosize from "react-textarea-autosize";
import Icon, { IconType } from "@/ui/Icon";
import { Camera, CameraResultType } from "@capacitor/camera";
import { Colors } from "@/constants/Colors";
import { useUISlice } from "@/models/useUISlice";
import { useState, lazy, Suspense, useEffect } from "react";
import { useUserSlice } from "@/models/useUserSlice";
import * as Types from "@/models/types";
import Menu from "@/ui/Menu";
import { getBasePath, getChatId } from "@/models/utils";
import { useUpload } from "@/models/useUpload";
import { useToast } from "@/models/useToast";
import { App } from "@capacitor/app";
import { isNativeApp } from "@/models/platform";
import { AUDIO_LANGUAGES } from "@/constants";
import { formatTime, useRecorder } from "@/models/useRecorder";
import { useAuth } from "@/models/useAuth";

// Lazy load the heavy ChatConversation component
const ChatConversation = lazy(() => import("@/ui/ChatConversation"));

// Loading fallback
const ConversationLoading = () => (
  <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
    <Text style={{ fontSize: ms(16), color: Colors.gray8 }}>Loading...</Text>
  </View>
);

function Chat() {
  const router = useRouter();
  const currentAIModel = useUISlice((state) => state.currentAIModel);
  const height = useUISlice((state) => state.device.height);
  const alertWidget = useUISlice((state) => state.alertWidget);
  const paywallVisible = useUISlice((state) => state.paywallVisible);
  const footerHeight = useUISlice((state) => state.footerHeight);
  const chatMessages = useUserSlice((state) => {
    const chatId = getChatId();
    if (!chatId) return [];

    return state.chats.find((chat) => chat.id === chatId)?.messages || [];
  });

  useEffect(() => {
    useUserSlice.getState().user().remoteConfig();

    if (isNativeApp()) {
      useAuth.signInWithMobile({
        callback: () => {
          useUserSlice.getState().user().profile();
          useUserSlice
            .getState()
            .subscription()
            .configureRevenueCat({
              callback: (isSubscribed) => {
                if (useUISlice.getState().shouldShowPaywall && !isSubscribed) {
                  useUISlice.getState().setPaywallVisible(true);
                  useUISlice.getState().setShouldShowPaywall(false);
                }
              },
            });
        },
      });
    } else {
      useAuth.signIn({
        forceSignIn: true,
        callback: () => {
          useUserSlice.getState().user().profile();
        },
      });
    }
  }, []);

  useEffect(() => {
    const listenBackButton = () => {
      App.addListener("backButton", () => {
        if (useUISlice.getState().alertWidget) {
          useUISlice.getState().setAlertWidget(undefined);
        } else if (useUISlice.getState().paywallVisible) {
          useUISlice.getState().setPaywallVisible(false);
        } else {
          App.minimizeApp();
        }
      });
    };

    listenBackButton();

    return () => {
      App.removeAllListeners();
    };
  }, [alertWidget, paywallVisible]);

  const _onSendMessage = (
    message: string,
    file?: { url: string; contentType: string; name?: string }
  ) => {
    const withSearch = useUISlice.getState().searchEnabled;
    useUserSlice
      .getState()
      .chat()
      .sendMessage({
        message: message.trim(),
        router,
        file,
        aiModelId: withSearch ? currentAIModel.searchModel : currentAIModel.id,
        withSearch,
      });
  };

  if (!currentAIModel) return <View />;

  return (
    <>
      <PageContainer HeaderComponent={<Chat.Header />}>
        <View
          style={{
            height: height - footerHeight - PageContainer.HEADER_HEIGHT,
            width: "100%",
            justifyContent: "center",
            alignSelf: "center",
            alignItems: "center",
          }}
        >
          {chatMessages.length > 0 ? (
            <Suspense fallback={<ConversationLoading />}>
              <ChatConversation messages={chatMessages} />
            </Suspense>
          ) : (
            <Chat.EmptyState currentAIModel={currentAIModel} />
          )}
        </View>
      </PageContainer>
      <Chat.Footer onSendMessage={_onSendMessage} />
    </>
  );
}

Chat.Header = () => {
  const router = useRouter();
  const { t } = useTranslation();

  const _goto = (path: string) => {
    // Add debounce to wait after menu close before routing
    const timeoutId = setTimeout(() => {
      router.push(path);
    }, 400);

    // Clear timeout if component unmounts
    return () => clearTimeout(timeoutId);
  };

  return (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        height: ms(60),
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: ms(12) }}>
        <Menu
          actions={[
            {
              icon: "pen",
              onPress: () => Chat.newChat(router),
              text: t("pageContainer.newChat"),
            },
            {
              icon: "history",
              onPress: () => _goto(`${getBasePath()}/history`),
              text: t("pageContainer.history"),
            },
            {
              icon: "cog",
              onPress: () => _goto(`${getBasePath()}/settings`),
              text: t("pageContainer.settings"),
            },
          ]}
          actionComponent={
            <Icon icon="hamburger" style={{ width: ms(26), height: ms(26) }} />
          }
        />

        <Pressable
          onPress={() => useUISlice.setState({ alertWidget: "selectAIModel" })}
          style={{ flexDirection: "row", alignItems: "center", gap: ms(6) }}
        >
          <Icon
            icon={useUISlice.getState().currentAIModel?.icon as IconType}
            style={{ width: ms(20), height: ms(20) }}
          />
          <Text style={{ fontSize: ms(18), fontWeight: "500" }}>
            {useUISlice.getState().currentAIModel?.name}
          </Text>
          <Text
            style={{
              fontSize: ms(16),
              color: Colors.text + "80",
              fontWeight: "400",
            }}
          >
            {useUISlice.getState().currentAIModel?.model}
          </Text>
          <Icon
            icon="caretDown"
            style={{
              width: ms(16),
              height: ms(16),
              tintColor: Colors.text,
            }}
          />
        </Pressable>
      </View>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: ms(12),
        }}
      >
        <Pressable
          onPress={() => Chat.newChat(router)}
          style={{
            width: ms(32),
            height: ms(32),
            borderRadius: ms(24),
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            icon="pen"
            style={{
              tintColor: Colors.text,
              width: ms(24),
              height: ms(24),
            }}
          />
        </Pressable>

        <Menu
          actions={[
            {
              onPress: () =>
                useUserSlice.getState().chat().copy({
                  chatId: getChatId(),
                }),
              text: t("pageContainer.copy"),
            },
            {
              onPress: () =>
                useUserSlice.getState().chat().share({
                  chatId: getChatId(),
                  type: "pdf",
                }),
              text: t("pageContainer.shareAsPDF"),
            },
            {
              onPress: () =>
                useUserSlice.getState().chat().share({
                  chatId: getChatId(),
                  type: "text",
                }),
              text: t("pageContainer.shareAsText"),
            },
            {
              onPress: () =>
                useUserSlice.getState().chat().share({
                  chatId: getChatId(),
                  type: "markdown",
                }),
              text: t("pageContainer.shareAsMarkdown"),
            },
          ]}
          actionComponent={
            <View
              style={{
                width: ms(32),
                height: ms(32),
                borderRadius: ms(24),
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon
                icon="moreHorizontal"
                style={{
                  transform: [{ rotate: "90deg" }],
                  tintColor: Colors.text,
                  width: ms(24),
                  height: ms(24),
                }}
              />
            </View>
          }
        />
      </View>
    </View>
  );
};

Chat.EmptyState = ({ currentAIModel }: { currentAIModel: Types.AIModel }) => {
  const { t } = useTranslation();

  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        gap: ms(12),
        justifyContent: "center",
        width: "100%",
      }}
    >
      <Icon
        icon={currentAIModel?.icon as IconType}
        style={{ width: ms(48), height: ms(48) }}
      />
      <Text
        style={{
          fontSize: ms(18),
          fontWeight: "500",
          textAlign: "center",
          marginTop: ms(12),
        }}
      >
        {t("chat.hi", { name: currentAIModel?.name })}{" "}
        <Text style={{ fontWeight: "400", color: Colors.text + "80" }}>
          {currentAIModel?.model}
        </Text>
      </Text>

      <Text
        style={{
          fontSize: ms(16),
          fontWeight: "400",
          color: Colors.text + "80",
        }}
      >
        {t("chat.description")}
      </Text>
    </View>
  );
};

Chat.Footer = ({
  onSendMessage,
}: {
  onSendMessage: (
    message: string,
    file?: { url: string; contentType: string; name?: string }
  ) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const searchEnabled = useUISlice((state) => state.searchEnabled);
  const { toast } = useToast();
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  const footerHeight = useUISlice((state) => state.footerHeight);
  const [transcribing, setTranscribing] = useState(false);
  const today = new Date().toISOString().split("T")[0];
  const freeAICreditLeft = Math.max(
    0,
    (useUserSlice.getState().remoteConfig?.dailyMessageLimit || 0) -
      (useUserSlice.getState().userData?.dailyMessagesCount?.[today] || 0)
  );
  const { openFilePicker } = useFileSelector();
  const [file, setFile] = useState<
    { url: string; contentType: string; name?: string } | undefined
  >(undefined);
  const { uploadFile, uploadProgress, isUploading, setUploadParams } =
    useUpload({
      maxFileSize: 5 * 1024 * 1024 * 1024, // 5GB
    });

  const {
    status,
    elapsedTime,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useRecorder();

  const _onSendMessage = () => {
    setMessage("");
    onSendMessage(message, file);
    setFile(undefined);
    useUISlice.getState().setFooterHeight("none");
  };

  const _getUploadedFileUrl = async (
    file: File
  ): Promise<string | undefined> => {
    if (!file) return;

    const fileExtension = file?.name?.split(".").pop() || "";
    setUploadParams({ fileType: file?.type, fileExtension });

    useUISlice.getState().setFooterHeight("uploading");
    const uploadResponse = await uploadFile(file);

    if (!uploadResponse.success) return;

    return uploadResponse.downloadUrl;
  };

  const _updateFile = (url: string, contentType: string, name?: string) => {
    useUISlice.getState().setFooterHeight(contentType);
    setFile({ url, contentType, name });
  };

  const _onTakePicture = async () => {
    const image = await Camera.getPhoto({
      quality: 70,
      allowEditing: false,
      resultType: CameraResultType.Base64,
    });

    if (!image?.base64String) return;

    const extension = image.format === "jpeg" ? ".jpg" : ".png";
    const type = image.format === "jpeg" ? "image/jpeg" : "image/png";
    _updateFile(image.webPath, type);

    const imageUrl = await _getUploadedFileUrl(
      new File(
        [Uint8Array.from(atob(image.base64String), (c) => c.charCodeAt(0))],
        `${new Date().getTime()}${extension}`,
        { type }
      )
    );

    if (imageUrl) {
      _updateFile(imageUrl, type);
    }
  };

  const _onSelectImage = async () => {
    const result = await Camera.pickImages({
      quality: 90,
      limit: 1,
    });

    const item = result?.photos?.[0];
    if (!item) return;

    const response = await fetch(item.webPath);
    const blob = await response.blob();
    const type = item.format === "jpeg" ? "image/jpeg" : "image/png";
    const extension = item.format === "jpeg" ? ".jpg" : ".png";
    _updateFile(item.webPath, type);
    const imageUrl = await _getUploadedFileUrl(
      new File([blob], `${new Date().getTime()}${extension}`, { type })
    );

    if (imageUrl) {
      _updateFile(imageUrl, type);
    }
  };

  const _onSelectFile = async () => {
    const supportedFileTypes = [".pdf"];
    const file = await openFilePicker(supportedFileTypes);

    if (!file) return;

    const fileExtension = file?.name?.split(".").pop();

    if (!supportedFileTypes.includes(`.${fileExtension}`)) {
      toast.error(t("chat.footerOnlyPDFFilesSupported"));
      return;
    }

    const fileUrl = await _getUploadedFileUrl(file);
    if (fileUrl) {
      _updateFile(fileUrl, file.type, file.name);
    }
  };

  const _onRemoveFile = () => {
    setFile(undefined);
    useUISlice.getState().setFooterHeight("none");
  };

  const _onToggleSearch = () => {
    if (!isSubscribed) {
      useUISlice.getState().setPaywallVisible(true);
      return;
    }

    useUISlice.getState().setSearchEnabled(!searchEnabled);
  };

  const _onStartRecording = () => {
    if (!isSubscribed) {
      useUISlice.getState().setPaywallVisible(true);
      return;
    }

    const transcribeLanguage =
      useUserSlice.getState().userData?.transcribeLanguage;

    if (transcribeLanguage) {
      startRecording();
      return;
    }

    useUISlice.getState().setAlertSelect({
      title: t("chat.selectLanguage"),
      options: AUDIO_LANGUAGES.map((language) => language.label),
      defaultOption: AUDIO_LANGUAGES.findIndex(
        (language) => language.value === transcribeLanguage
      ),
      onSelect: (index) => {
        const languageCode = AUDIO_LANGUAGES[index].value;
        useUserSlice
          .getState()
          .user()
          .update({
            transcribeLanguage:
              languageCode?.slice(0, 2)?.toLowerCase() || "en",
          });
        startRecording();
      },
    });
  };

  const _onStopRecording = async () => {
    setTranscribing(true);

    // use recorder
    await (stopRecording as ReturnType<typeof useRecorder>["stopRecording"])(
      async (file) => {
        const audioUrl = await _getUploadedFileUrl(file);
        if (!audioUrl) return;

        const transcript = await useUserSlice.getState().chat().transcribe({
          fileUrl: audioUrl,
          languageCode: useUserSlice.getState().userData?.transcribeLanguage,
        });

        setMessage(transcript);
        setTranscribing(false);
      }
    );
  };

  const _onCancelRecording = () => {
    cancelRecording(() => {});
  };

  return (
    <View
      style={{
        flex: 1,
        bottom: 0,
        left: 0,
        backgroundColor: Colors.white,
        maxHeight: footerHeight,
        width: "100%",
        paddingHorizontal: ms(12),
        paddingBottom: ms(20),
        ...({ position: "fixed" } as any),
      }}
    >
      <View
        style={{
          borderRadius: ms(20),
          borderWidth: 1,
          borderColor: Colors.text + "40",
          paddingTop: file || (isUploading && !transcribing) ? ms(12) : ms(2),
          paddingLeft: ms(12),
          paddingRight: ms(12),
          paddingBottom: ms(8),
        }}
      >
        {isUploading && !transcribing && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginBottom: ms(8),
              width: Chat.ImageSize - ms(12),
              height: Chat.ImageSize - ms(12),
              borderRadius: ms(12),
              backgroundColor: Colors.gray6,
              justifyContent: "center",
            }}
          >
            <ActivityIndicator size="small" color={Colors.white} />
          </View>
        )}
        {file?.contentType?.includes("image") && !isUploading && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginBottom: ms(8),
              width: Chat.ImageSize - ms(12),
              height: Chat.ImageSize - ms(12),
              borderRadius: ms(12),
              backgroundColor: Colors.gray6,
              justifyContent: "center",
            }}
          >
            <View style={{ position: "relative" }}>
              <Image
                source={{ uri: file.url }}
                style={{
                  width: Chat.ImageSize - ms(12),
                  height: Chat.ImageSize - ms(12),
                  borderRadius: ms(12),
                }}
              />

              <Pressable
                onPress={_onRemoveFile}
                style={{
                  position: "absolute",
                  top: ms(4),
                  right: ms(4),
                  backgroundColor: Colors.white,
                  borderRadius: ms(2),
                  shadowColor: Colors.text + "08",
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.25,
                  shadowRadius: 3.84,
                  elevation: 5,
                }}
              >
                <Icon
                  icon="dismiss"
                  style={{
                    width: ms(20),
                    height: ms(20),
                    borderRadius: ms(10),
                    padding: ms(4),
                  }}
                />
              </Pressable>
            </View>
          </View>
        )}

        {file?.url?.toLowerCase()?.endsWith(".pdf") && !isUploading && (
          <View style={{ position: "relative", width: "80%" }}>
            <View
              style={{
                height: ms(60),
                backgroundColor: Colors.text + "20",
                borderRadius: ms(16),
                flexDirection: "row",
                gap: ms(12),
                alignItems: "center",
                paddingHorizontal: ms(12),
              }}
            >
              <Icon
                icon="document"
                containerStyle={{
                  backgroundColor: Colors.text,
                  padding: ms(6),
                  borderRadius: ms(8),
                  alignItems: "center",
                }}
                style={{
                  width: ms(24),
                  height: ms(24),
                  tintColor: Colors.white,
                }}
              />

              <View style={{ flex: 1, gap: ms(4) }}>
                <Text
                  numberOfLines={1}
                  style={{
                    fontSize: ms(14),
                    fontWeight: "500",
                    color: Colors.text,
                  }}
                >
                  {file.name}
                </Text>

                <Text
                  style={{
                    fontSize: ms(12),
                    fontWeight: "400",
                    color: Colors.text + "80",
                  }}
                >
                  {isUploading ? t("chat.uploading") : t("chat.uploaded")}
                </Text>
              </View>
            </View>

            <Pressable
              onPress={_onRemoveFile}
              style={{
                position: "absolute",
                top: ms(8),
                right: ms(8),
                width: ms(20),
                height: ms(20),
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: Colors.text,
                borderRadius: ms(12),
                shadowColor: Colors.text + "08",
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.25,
                shadowRadius: 3.84,
                elevation: 5,
              }}
            >
              <Icon
                icon="dismiss"
                style={{
                  width: ms(16),
                  height: ms(16),
                  tintColor: Colors.white,
                }}
              />
            </Pressable>
          </View>
        )}

        <TextareaAutosize
          placeholder={t("chat.footerPlaceholder")}
          style={{
            fontSize: ms(16),
            color: Colors.text,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            fontWeight: "400",
            paddingTop: ms(16),
            paddingBottom: ms(16),
            outline: "none",
            resize: "none",
            borderWidth: 0,
            backgroundColor: "transparent",
          }}
          minRows={1}
          maxRows={2}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        {transcribing && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: ms(12),
              height: ms(32),
              paddingLeft: ms(4),
            }}
          >
            <ActivityIndicator size="small" color={Colors.text} />
            <Text
              style={{
                fontSize: ms(16),
                fontWeight: "400",
                color: Colors.gray8,
              }}
            >
              {t("chat.convertingToText")}
            </Text>
          </View>
        )}

        {!transcribing && status === "recording" && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: ms(12),
              }}
            >
              <Pressable
                onPress={_onCancelRecording}
                style={{
                  width: ms(32),
                  height: ms(32),
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  icon="dismiss"
                  style={{ width: ms(24), height: ms(24) }}
                />
              </Pressable>

              <Text
                style={{
                  fontSize: ms(14),
                  fontWeight: "500",
                  color: Colors.text,
                }}
              >
                {formatTime(elapsedTime)}
              </Text>
            </View>

            <Pressable
              onPress={_onStopRecording}
              style={{
                width: ms(40),
                height: ms(40),
                borderRadius: ms(24),
                backgroundColor: Colors.accent,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon
                icon="tick"
                style={{
                  width: ms(20),
                  height: ms(20),
                  tintColor: Colors.white,
                }}
              />
            </Pressable>
          </View>
        )}

        {!transcribing && status !== "recording" && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: ms(4),
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: ms(12),
              }}
            >
              <Menu
                actions={[
                  {
                    icon: "camera",
                    onPress: _onTakePicture,
                    text: t("chat.footerTakePhoto"),
                  },
                  {
                    icon: "picture",
                    onPress: _onSelectImage,
                    text: t("chat.footerAddPhoto"),
                  },
                  {
                    icon: "fileAdd",
                    onPress: _onSelectFile,
                    text: t("chat.footerAddFiles"),
                  },
                ]}
                actionComponent={
                  <Icon
                    icon="plus"
                    style={{
                      width: ms(28),
                      height: ms(28),
                      tintColor: Colors.gray8,
                    }}
                  />
                }
              />

              <Pressable
                onPress={_onToggleSearch}
                style={{
                  height: ms(40),
                  paddingHorizontal: ms(12),
                  borderRadius: ms(24),
                  flexDirection: "row",
                  backgroundColor: Colors.text + "08",
                  alignItems: "center",
                  gap: ms(4),
                  ...(searchEnabled && {
                    backgroundColor: Colors.accent + "10",
                  }),
                }}
              >
                <Icon
                  icon="world"
                  style={{
                    width: ms(20),
                    height: ms(20),
                    tintColor: Colors.gray8,
                    ...(searchEnabled && {
                      tintColor: Colors.accent,
                    }),
                  }}
                />
                <Text
                  style={{
                    fontSize: ms(14),
                    fontWeight: "500",
                    color: Colors.gray8,
                    ...(searchEnabled && {
                      color: Colors.accent,
                    }),
                  }}
                >
                  {t("chat.footerSearch")}
                </Text>

                {!isSubscribed && (
                  <Text
                    style={{
                      position: "absolute",
                      top: -ms(8),
                      right: -ms(2),
                      fontSize: ms(16),
                    }}
                  >
                    🔒
                  </Text>
                )}
              </Pressable>
            </View>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: ms(20),
              }}
            >
              <Pressable onPress={_onStartRecording} style={{}}>
                <Icon
                  icon="mic"
                  style={{
                    width: ms(20),
                    height: ms(20),
                    tintColor: Colors.gray8,
                  }}
                />
              </Pressable>

              <Pressable
                disabled={message.length === 0}
                onPress={_onSendMessage}
                style={{
                  width: ms(40),
                  height: ms(40),
                  borderRadius: ms(24),
                  backgroundColor:
                    message.length === 0 ? Colors.accent + "30" : Colors.accent,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  icon="send"
                  style={{
                    width: ms(20),
                    height: ms(20),
                    tintColor: Colors.white,
                  }}
                />

                {!isSubscribed && freeAICreditLeft < 5 && (
                  <Text
                    style={{
                      position: "absolute",
                      top: -ms(8),
                      right: -ms(4),
                      fontSize: ms(10),
                      fontWeight: "600",
                      color: Colors.white,
                      backgroundColor: Colors.text,
                      paddingHorizontal: ms(6),
                      paddingVertical: ms(3),
                      borderRadius: ms(12),
                    }}
                  >
                    {freeAICreditLeft}
                  </Text>
                )}
              </Pressable>
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

Chat.newChat = (router: ReturnType<typeof useRouter>) => {
  router.replace("/");
};

Chat.ImageSize = ms(90);

export default Chat;
